/**
 * Platform yönetim panelinin (backoffice, `apps/backoffice`; ADR-002, S16) metinleri. Arayüzde müşteri "İşletme"dir
 * (08 §9.1); kimlik doğrulama "Oturum aç / Oturumu kapat"tır. Hitap "siz"dir. Backoffice'e özgü hata iletileri
 * `errors` altındadır ve genel katalogdan (`errors.*`) önce okunur: genel iletiler şirket kullanıcısına yöneliktir
 * ("şirket yöneticinizden isteyin").
 */
export const backoffice = {
  title: '{page} · TicariShift Platform',
  appName: 'TicariShift Platform',
  badge: 'Platform',
  environment: {
    label: 'Ortam: {name}',
    local: 'Yerel',
    production: 'Prod',
  },
  roles: {
    platform_owner: 'Platform sahibi',
    platform_admin: 'Platform yöneticisi',
    platform_support: 'Platform destek',
    platform_billing_read: 'Faturalama (salt okuma)',
  },
  roleDescriptions: {
    platform_owner: 'Bütün işlemler: işletme kapatma ve platform kullanıcılarını yönetme dahil.',
    platform_admin: 'İşletme açma, askıya alma ve yeniden açma; plan kataloğu, abonelik, faturalama eylemleri, kota ve bayraklar.',
    platform_support: 'Salt okuma: işletmeler, sistem özeti ve denetim kaydı; işletmenin izniyle salt okuma destek oturumu.',
    platform_billing_read: 'Salt okuma: işletme listesi, faturalama izleme ve gelir göstergeleri.',
  },
  nav: {
    label: 'Platform menüsü',
    // S31: backoffice panelin kabuk metinlerini (`shell`) paketlemez; breadcrumb'ın adı burada.
    breadcrumb: 'Bulunduğunuz yer',
    open: 'Menüyü aç',
    overview: 'Genel Bakış',
    tenants: 'İşletmeler',
    tenantDetail: 'İşletme',
    // S41 (YH-S57): plan kataloğu, ek paketler ve paket × özellik matrisi.
    plans: 'Planlar',
    // S43 (bölüm 6): faturalama izleme, gelir göstergeleri ve müşavir pay raporu.
    billing: 'Faturalama',
    flags: 'Bayraklar',
    users: 'Platform kullanıcıları',
    audit: 'Denetim kaydı',
    legal: 'Yasal parametreler',
    legalSet: 'Sürüm',
    holidays: 'Resmî tatil takvimi',
    holidayYear: 'Yıl',
    holidayVersion: 'Sürüm',
    // S28 (ADR-002 §2.5): QR Ekranı ve kiosk sağlığı; yalnız sayı ve teknik satır.
    screenHealth: 'QR Ekranı sağlığı',
    // S31 (YH-S52): sahip hesap kurtarma talepleri ve iki operatör onayı.
    ownerRecovery: 'Sahip hesap kurtarma',
    // S40 (SK-04, PY-57): kullanım ve sistem sağlığı panoları; platform duyurusu ve bakım bildirimi.
    usage: 'Kullanım',
    health: 'Sistem sağlığı',
    announcements: 'Duyurular',
    announcement: 'Duyuru',
    // S44 (YH-S61): kapanan işletmelerin iade paketi ve silme kuyruğu (izleme).
    tenantExits: 'Silme kuyruğu',
    tenantExitDetail: 'Kapanan işletme',
    // S44: hukuki metin sürümleri (taslak, yayın) ve alt işleyen listesi.
    legalDocuments: 'Hukuki metinler',
    legalDocument: 'Metin',
    subprocessors: 'Alt işleyenler',
    // S44 (ADR-002 §2.4, SK-04): müşteri onaylı, süre sınırlı ve salt okuma destek oturumu.
    support: 'Destek erişimi',
    // S62-d (SK-21): platformun e-posta gönderdiği SMTP sunucusu.
    emailSettings: 'E-posta (SMTP)',
  },
  // S40: bildirim kanallarının adları (kullanım ve sistem sağlığı panoları; 07 §8.4).
  channels: {
    InApp: 'Uygulama içi',
    Email: 'E-posta',
    Sms: 'SMS',
    Push: 'Anlık bildirim',
    WhatsApp: 'WhatsApp',
    Webhook: 'Webhook',
  },
  account: {
    menu: 'Hesap menüsü',
    signOut: 'Oturumu kapat',
    theme: {
      label: 'Tema',
      Light: 'Açık',
      Dark: 'Koyu',
      System: 'Sistem',
      failed: 'Tema tercihiniz kaydedilemedi. Tekrar deneyin.',
    },
  },
  system: {
    loading: 'Yükleniyor…',
    resolving: 'Platform yetkileriniz okunuyor…',
    connectionTitle: 'Bağlantı kurulamadı',
    connectionBody: 'Sunucuya ulaşamadık. Bağlantınız gelince kendiliğinden yeniden denenir.',
    otherAccount: 'Farklı hesapla oturum aç',
    forbiddenTitle: 'Bu sayfa platform rolünüze açık değil',
    forbiddenBody: 'Bu sayfa için "{permission}" yetkisi gerekir. Platform sahibinden rolünüzü değiştirmesini isteyin.',
    notFoundTitle: 'Sayfa bulunamadı',
    notFoundBody: 'Adres yanlış yazılmış ya da sayfa kaldırılmış olabilir.',
    goHome: 'Ana sayfaya dön',
  },
  /** Yetki adları: anahtar yetkinin kendisidir (`backoffice.permissions.platform.tenants.read`). */
  permissions: {
    platform: {
      tenants: {
        read: 'İşletmeleri görme',
        manage: 'İşletme açma, askıya alma ve yeniden açma',
        close: 'İşletme kapatma',
      },
      entitlements: { manage: 'Özellik ve kota geçersiz kılma, bayrak değiştirme' },
      plans: { manage: 'Plan kataloğunu ve işletme aboneliklerini yönetme' },
      users: { manage: 'Platform kullanıcılarını yönetme' },
      audit: { read: 'Platform denetim kaydını görme' },
      system: { read: 'Sistem özetini görme' },
      outbox: { replay: 'Teslim edilemeyen mesajı yeniden oynatma' },
      billing: {
        read: 'Faturalama izlemeyi ve gelir göstergelerini görme',
        manage: 'Havale kaydı, iade, yeniden tahsil ve faturalama uyarısını kapatma',
      },
      // S44: aynı yetkiler hukuki metin sürümlerini ve alt işleyen listesini de kapsar.
      legal: {
        read: 'Yasal parametreleri, tatil takvimini, hukuki metinleri ve alt işleyenleri görme',
        manage: 'Yasal parametre, tatil takvimi ve hukuki metin taslağı açma ve yayınlama; alt işleyenleri düzenleme',
      },
      owner_recovery: {
        request: 'Sahip hesap kurtarma talebi açma ve görme',
        decide: 'Sahip hesap kurtarma talebini onaylama ya da reddetme',
      },
      announcements: { manage: 'Sistem duyurusu ve bakım bildirimi yazma, yayınlama ve iptal etme' },
      support: { session: 'İşletmenin izniyle salt okuma destek oturumu başlatma ve bitirme' },
      settings: { email: 'E-posta (SMTP) ayarını görme, kaydetme ve deneme e-postası gönderme' },
    },
  },
  auth: {
    shellTagline: 'Platform yönetimi',
    shellBody: 'İşletmeler, planlar ve platform kullanıcıları bu panelden yönetilir. Her oturum iki adımlı doğrulama ister.',
    help: 'Oturum açamıyorsanız platform sahibine başvurun.',
    titles: {
      login: 'Oturum aç',
      activate: 'Platform daveti',
    },
    notices: {
      expired: 'Oturumunuzun süresi doldu. Devam etmek için yeniden oturum açın.',
      disabled: 'Platform erişiminiz kapatıldı. Bir yanlışlık olduğunu düşünüyorsanız platform sahibine başvurun.',
      signedOut: 'Oturumunuz kapatıldı.',
      restart: 'Oturum açma adımının süresi doldu. Baştan oturum açın.',
    },
    credentials: {
      title: 'Platform yönetimine oturum açın',
      body: 'Platform hesabınızın e-posta adresi ve şifresiyle devam edin.',
      email: 'E-posta',
      emailRequired: 'E-posta adresinizi yazın.',
      emailInvalid: 'Geçerli bir e-posta adresi yazın.',
      password: 'Şifre',
      passwordRequired: 'Şifrenizi yazın.',
      submit: 'Oturum aç',
    },
    changePassword: {
      title: 'Kendi şifrenizi belirleyin',
      body: 'Hesabınız başlangıç şifresiyle açıldı. Devam etmek için yalnız sizin bildiğiniz yeni bir şifre belirleyin.',
      newPassword: 'Yeni şifre',
      confirm: 'Yeni şifre (tekrar)',
      hint: 'En az {min} karakter. Kolay tahmin edilmeyen bir kelime grubu seçin.',
      required: 'Yeni şifreyi yazın.',
      tooShort: 'Şifre en az {min} karakter olmalı.',
      tooLong: 'Şifre en fazla {max} karakter olabilir.',
      mismatch: 'Şifreler eşleşmiyor.',
      submit: 'Şifreyi kaydet ve devam et',
    },
    mfa: {
      title: 'İki adımlı doğrulama',
      body: 'Doğrulayıcı uygulamanızdaki {length} haneli kodu girin.',
      recoveryBody: 'Kurtarma kodlarınızdan birini yazın. Her kod yalnız bir kez kullanılır.',
      codeLabel: 'Doğrulama kodu',
      recoveryLabel: 'Kurtarma kodu',
      required: 'Kodu yazın.',
      length: 'Kod {length} haneli olmalı.',
      useRecovery: 'Kurtarma kodu kullan',
      useTotp: 'Doğrulayıcı uygulamadaki kodu kullan',
      submit: 'Oturum aç',
    },
    enroll: {
      title: 'İki adımlı doğrulamayı kurun',
      body: 'Platform hesaplarında iki adımlı doğrulama zorunludur ve bu adım atlanamaz. Doğrulayıcı uygulamayla (Google Authenticator, Microsoft Authenticator gibi) kodu okutun, ardından uygulamanın gösterdiği {length} haneli kodu girin.',
      qrLabel: 'Doğrulayıcı uygulamaya eklenecek QR kodu',
      manual: 'Kodu okutamıyorsanız bu anahtarı uygulamaya elle yazın:',
      secretLabel: 'Kurulum anahtarı',
      submit: 'Doğrula ve devam et',
    },
    recoveryCodes: {
      title: 'Kurtarma kodlarınızı saklayın',
      body: 'Telefonunuza erişemezseniz bu kodlarla oturum açarsınız. Her kod bir kez kullanılır. Kodlar yalnız şimdi gösterilir; bu sayfadan çıkınca bir daha görülmez.',
      listLabel: 'Kurtarma kodları',
      copy: 'Kodları kopyala',
      copied: 'Kodlar kopyalandı.',
      download: 'Dosya olarak indir',
      fileName: 'ticarishift-platform-kurtarma-kodlari.txt',
      fileHeader: 'TicariShift Platform kurtarma kodları. Her kod bir kez kullanılır.',
      confirm: 'Kodları kaydettim, devam et',
    },
    startOver: 'Baştan başla',
    activate: {
      title: 'Platform davetinizi kabul edin',
      body: 'Davet, e-posta adresinize bir platform rolüyle açıldı. Kabul ettikten sonra oturum açıp iki adımlı doğrulamayı kurarsınız.',
      password: 'Şifre',
      passwordHint: 'Bu e-postayla açılmış bir TicariShift hesabınız varsa mevcut şifrenizi yazın. Yoksa en az {min} karakterlik yeni bir şifre belirleyin.',
      passwordRequired: 'Şifrenizi yazın.',
      submit: 'Daveti kabul et',
      doneTitle: 'Davetiniz kabul edildi',
      doneBody: 'Şimdi oturum açın. İlk oturumda iki adımlı doğrulamayı kuracak ve kurtarma kodlarınızı alacaksınız.',
      signIn: 'Oturum aç',
      invalidTitle: 'Bağlantı geçersiz',
      expiredTitle: 'Davetin süresi dolmuş',
      usedTitle: 'Bu davet zaten kabul edildi',
      invalidBody: 'Platform sahibinden yeni bir davet isteyin.',
      usedBody: 'Hesabınızla oturum açabilirsiniz.',
    },
  },
  overview: {
    // S62-d (SK-21): SMTP girilene kadar e-posta pasiftir; davet ve doğrulama kodu e-postaları kuyrukta bekler.
    emailNotConfigured: 'E-posta gönderimi kapalı: SMTP bilgileri girilmedi. Davet, doğrulama kodu ve bildirim e-postaları kuyrukta bekliyor; bilgiler girilince gönderilir.',
    emailNotConfiguredAction: 'SMTP bilgilerini girin',
    title: 'Genel Bakış',
    description: 'Platformun anlık durumu: işletmeler, oturumlar, mesaj kuyruğu, zamanlanmış işler ve sistem sağlığı.',
    generatedAt: 'Son güncelleme {time}',
    refresh: 'Yenile',
    stats: {
      trial: 'Denemedeki işletme',
      active: 'Açık işletme',
      pastDue: 'Ödemesi geciken işletme',
      readOnly: 'Salt okunur işletme',
      suspended: 'Askıdaki işletme',
      closed: 'Kapalı işletme',
      total: 'Toplam {count}',
      signedIn: 'Son 24 saatte oturum açan',
      signedInHint: 'İşletme ve platform kullanıcıları',
      signedInHelp: 'Son 24 saatte en az bir kez oturum açan farklı kullanıcı sayısı (işletme ve platform üyelikleri).',
      openTenants: '{label}: {count}. Listeyi aç',
    },
    outbox: {
      title: 'Mesaj kuyruğu (outbox)',
      pending: 'Bekleyen',
      processing: 'İşleniyor',
      deadLetters: 'Teslim edilemeyen (DeadLetter)',
      oldest: 'En eski bekleyenin yaşı',
      none: 'Bekleyen yok',
      seconds: '{value} sn',
      deadLettersHint: 'Teslim edilemeyen mesaj var. Yeniden oynatma ekranı sonraki sürümde gelir; kimlikle yeniden oynatma API\'dedir.',
    },
    jobs: {
      title: 'Zamanlanmış işler',
      caption: 'Zamanlanmış işlerin son ve sonraki koşusu',
      key: 'İş',
      scope: 'Kapsam',
      lastRun: 'Son koşu',
      result: 'Sonuç',
      nextRun: 'Sonraki koşu',
      disabled: 'Kapalı',
      never: 'Henüz koşmadı',
      empty: 'Kayıtlı zamanlanmış iş yok.',
      scopes: {
        Global: 'Platform geneli',
        PerTenant: 'İşletme başına',
      },
      status: {
        succeeded: 'Başarılı',
        failed: 'Başarısız',
        running: 'Sürüyor',
        other: '{status}',
        none: 'Koşu yok',
      },
    },
    health: {
      title: 'Sistem sağlığı',
      caption: 'Hazırlık denetiminin (/health/ready) bileşenleri',
      overall: 'Genel durum',
      component: 'Bileşen',
      status: 'Durum',
      duration: 'Süre',
      durationValue: '{ms} ms',
      empty: 'Bileşen bilgisi yok.',
      states: {
        Healthy: 'Sağlıklı',
        Degraded: 'Yavaşladı',
        Unhealthy: 'Çalışmıyor',
      },
    },
  },
  tenants: {
    title: 'İşletmeler',
    description: 'Platformdaki işletmeler. Kişi verisi gösterilmez; yalnız sayılar ve özet vardır.',
    create: 'Yeni işletme',
    caption: 'İşletmeler listesi',
    searchLabel: 'İşletme ara',
    searchPlaceholder: 'Ad ya da kısa ad',
    filters: {
      status: 'Durum',
      // S72: uyku tenant durumu değildir; ayrı süzgeçtir.
      sleep: 'Uyku',
    },
    sleep: {
      asleep: 'Uykuda',
      awake: 'Uyanık',
      badge: 'Uykuda',
    },
    columns: {
      name: 'Ad',
      slug: 'Kısa ad',
      status: 'Durum',
      plan: 'Plan',
      createdVia: 'Açılış',
      createdAt: 'Açılış tarihi',
    },
    status: {
      Trial: 'Deneme',
      Active: 'Açık',
      PastDue: 'Ödeme gecikti',
      ReadOnly: 'Salt okunur',
      Suspended: 'Askıda',
      Closed: 'Kapalı',
    },
    createdVia: {
      Platform: 'Platform',
      SelfSignup: 'Kendi kaydı',
    },
    // S41: planın kademe kodu (`Tenant.PlanCode`; liste, duyuru hedefi). Planın sürümlü adı katalogdadır (`GET /api/platform/plans`).
    plans: {
      free: 'Ücretsiz',
      starter: 'Başlangıç',
      pro: 'Pro',
      enterprise: 'Kurumsal',
    },
    empty: {
      title: 'Henüz işletme yok',
      body: 'İlk işletmeyi açın; İşletme sahibine aktivasyon daveti e-postayla gider.',
    },
    new: {
      title: 'Yeni işletme',
      description: 'İşletme tek açılış servisinden açılır; İşletme sahibine aktivasyon daveti e-postayla gider.',
      name: 'İşletme adı',
      slug: 'Kısa ad',
      slugHint: 'Adreslerde ve kayıtlarda görünür. Küçük harf, rakam ve tire kullanın; addan önerilir.',
      slugInvalid: 'Kısa ad yalnız küçük harf, rakam ve tire içerebilir; tireyle başlayıp bitemez.',
      slugTaken: 'Bu kısa ad kullanımda. Başka bir kısa ad seçin.',
      timezone: 'Saat dilimi',
      plan: 'Plan',
      planHint: 'İşletme bu planın güncel sürümüyle, deneme olmadan açılır. Fiyatlar aktif çalışan başına aylık ve KDV hariçtir. Planı sonra İşletme detayının Abonelik sekmesinden değiştirebilirsiniz.',
      owner: 'İşletme sahibi',
      ownerHint: 'Davet bu e-posta adresine gider. Sahibin bilgileri işletmenin kendi kaydında tutulur; platform ekranlarında gösterilmez.',
      ownerFirstName: 'Ad',
      ownerLastName: 'Soyad',
      ownerEmail: 'E-posta',
      nameRequired: 'İşletme adını yazın.',
      nameLength: 'İşletme adı 2 ile 200 karakter arasında olmalı.',
      slugRequired: 'Kısa adı yazın.',
      slugLength: 'Kısa ad 2 ile 100 karakter arasında olmalı.',
      firstNameRequired: 'İşletme sahibinin adını yazın.',
      lastNameRequired: 'İşletme sahibinin soyadını yazın.',
      nameMin: 'En az 2 karakter yazın.',
      emailRequired: 'İşletme sahibinin e-posta adresini yazın.',
      emailInvalid: 'Geçerli bir e-posta adresi yazın.',
      submit: 'İşletmeyi aç',
      created: '{name} açıldı. İşletme sahibine davet e-postayla gönderiliyor.',
    },
    timezones: {
      'Europe/Istanbul': 'Türkiye (İstanbul)',
      'Europe/Berlin': 'Almanya (Berlin)',
      'Europe/London': 'Birleşik Krallık (Londra)',
      'Asia/Baku': 'Azerbaycan (Bakü)',
      'Asia/Nicosia': 'Kıbrıs (Lefkoşa)',
    },
  },
  tenant: {
    back: 'İşletmelere dön',
    tabs: {
      general: 'Genel',
      subscription: 'Abonelik',
      entitlements: 'Haklar ve kotalar',
      audit: 'Denetim',
      screens: 'QR Ekranları',
      recovery: 'Sahip hesap kurtarma',
      usage: 'Kullanım',
      support: 'Destek erişimi',
    },
    loadFailed: 'İşletme yüklenemedi.',
    notFoundTitle: 'İşletme bulunamadı',
    notFoundBody: 'Adres yanlış olabilir.',
    closedNotice: 'Bu işletme kapatıldı. Kullanıcıları oturum açamaz; veri silinmez. Kapatılan işletme yeniden açılamaz.',
    // S72: hiç kullanılmamış Ücretsiz işletme kapanış uyarısından sonra kendiliğinden kapanır.
    unusedClosedNotice: 'Bu işletme {date} tarihinde, hiç kullanılmadığı için kendiliğinden kapandı.',
    asleepNotice:
      'Bu işletme uykuda: Ücretsiz planda uzun süredir kullanılmıyor. Hatırlatma ve hesap işleri durdu; saklama ve silme işleri sürer. İlk oturumda ya da mesai kaydında kendiliğinden uyanır.',
    suspendedNotice: 'Bu işletme askıda. Kullanıcıları oturum açamaz; QR Ekranları ve zamanlanmış işler çalışmaz.',
    summary: {
      title: 'Özet',
      status: 'Durum',
      plan: 'Plan',
      slug: 'Kısa ad',
      timezone: 'Saat dilimi',
      createdVia: 'Açılış yolu',
      createdAt: 'Açılış tarihi',
      lastActivity: 'Son aktivite',
      lastActivityHelp: 'İşletmede son oturum açılan an.',
      never: 'Henüz yok',
      // S72: uyku ve kullanılmayan hesabın kapanışı.
      sleep: 'Uyku',
      asleepSince: '{date} tarihinden beri uykuda',
      awake: 'Uyanık',
    },
    usage: {
      employees: 'Aktif çalışan',
      members: 'Etkin kullanıcı',
      owners: 'Etkin İşletme sahibi',
      ownerPending: 'Sahip daveti bekliyor',
    },
    history: {
      title: 'Durum geçmişi',
      caption: 'İşletmenin durum geçmişi',
      when: 'Tarih',
      change: 'Değişiklik',
      reason: 'Gerekçe',
      actor: 'Yapan',
      created: 'Açıldı: {to}',
      transition: '{from} → {to}',
      system: 'Sistem',
      empty: 'Durum değişikliği yok.',
    },
    actions: {
      label: 'İşletme eylemleri',
      suspend: 'Askıya al',
      reactivate: 'Yeniden aç',
      close: 'Kapat',
      reinvite: 'Sahibi yeniden davet et',
    },
    suspend: {
      title: '{name} askıya alınsın mı?',
      body: 'İşletmenin bütün kullanıcılarının oturumu kapanır ve bir sonraki istekte askı ekranını görürler. QR Ekranları ve zamanlanmış işler durur. Veri silinmez; işletmeyi yeniden açabilirsiniz.',
      done: '{name} askıya alındı.',
    },
    reactivate: {
      title: '{name} yeniden açılsın mı?',
      body: 'Kullanıcılar yeniden oturum açarak devam eder; askıdan önceki oturumlar geri gelmez.',
      done: '{name} yeniden açıldı.',
    },
    close: {
      title: '{name} kapatılsın mı?',
      body: 'Kapatılan işletme yeniden açılamaz. Kullanıcıları oturum açamaz; veri silinmez ve saklama süresi boyunca korunur.',
      done: '{name} kapatıldı.',
    },
    reinvite: {
      title: 'İşletme sahibi yeniden davet edilsin mi?',
      body: 'Tamamlanmamış İşletme sahibi daveti yenilenir: eski bağlantı geçersiz olur, yenisi sahibin e-postasına gider. Yeni bağlantıyı bir kez burada da görürsünüz.',
      confirm: 'Yeniden davet et',
      doneTitle: 'Davet yenilendi',
      doneBody: 'Bağlantı sahibin e-postasına gönderildi. Gerekirse aşağıdaki bağlantıyı güvenli bir kanaldan iletin; bu pencere kapanınca bağlantı bir daha gösterilmez.',
      link: 'Aktivasyon bağlantısı',
      expires: 'Son geçerlilik: {time}',
      close: 'Kapat',
    },
    reason: {
      label: 'Gerekçe',
      hint: 'Platform denetim kaydına ve işletmenin kendi denetim kaydına yazılır.',
    },
  },
  entitlements: {
    title: 'Haklar ve kotalar',
    description: 'Planın hakları, platform geçersiz kılmaları ve kotalar. Değişiklik bir sonraki istekte etkilidir; kapatılan özellikte veri silinmez.',
    readOnly: 'Rolünüz bu bölümü yalnız görmeye izin veriyor.',
    plan: 'Plan',
    planValue: '{name} · {version}. sürüm',
    planHint: 'Plan, sürüm ve ek paketler Abonelik sekmesinden atanır.',
    openSubscription: 'Aboneliği aç',
    features: {
      title: 'Özellikler',
      caption: 'İşletmenin özellikleri ve geçersiz kılmalar',
      feature: 'Özellik',
      plan: 'Planda',
      platformFlag: 'Platform bayrağı',
      override: 'Geçersiz kılma',
      effective: 'Sonuç',
      tenantDisabled: 'İşletme kapattı',
      overrideLabel: '{feature} için geçersiz kılma',
      options: {
        inherit: 'Plana göre',
        on: 'Açık',
        off: 'Kapalı',
      },
      overrideReason: 'Son gerekçe: {reason}',
    },
    limits: {
      title: 'Kotalar',
      caption: 'İşletmenin kotaları',
      limit: 'Kota',
      plan: 'Plan sınırı',
      kind: 'Tür',
      hard: 'Sert',
      soft: 'Yumuşak',
      kindHint: 'Sert sınırda yeni kayıt engellenir; yumuşak sınırda yalnız uyarı verilir. Mesai kaydı hiçbir sınırda engellenmez.',
      override: 'Platform değeri',
      effective: 'Sonuç',
      unlimited: 'Sınırsız',
      gigabytes: 'GB',
      overrideLabel: '{limit} için platform değeri',
      overrideLabelGb: '{limit} için platform değeri (GB)',
      overrideHint: 'Boş bırakırsanız plan sınırı geçerlidir. Depolama GB olarak yazılır.',
      invalid: 'Kota 0 ya da daha büyük bir tam sayı olmalı.',
    },
    yes: 'Evet',
    no: 'Hayır',
    on: 'Açık',
    off: 'Kapalı',
    reason: 'Gerekçe',
    reasonHint: 'Platform denetim kaydına ve işletmenin kendi denetim kaydına yazılır.',
    reasonRequired: 'Değişikliğin gerekçesini yazın.',
    save: 'Değişiklikleri kaydet',
    discard: 'Değişiklikleri at',
    noChanges: 'Kaydedilecek değişiklik yok.',
    saved: 'Haklar ve kotalar kaydedildi.',
  },
  flags: {
    title: 'Bayraklar',
    description: 'Platform özellik bayrakları bütün işletmeleri etkiler. Kapatılan özellik bir sonraki istekte kapanır; veri silinmez.',
    caption: 'Platform özellik bayrakları',
    readOnly: 'Rolünüz bayrakları yalnız görmeye izin veriyor.',
    columns: {
      flag: 'Bayrak',
      state: 'Durum',
      tenantOptional: 'İşletme kapatabilir',
      note: 'Not',
      updated: 'Son değişiklik',
    },
    toggleLabel: '{flag} bayrağı',
    on: 'Açık',
    off: 'Kapalı',
    yes: 'Evet',
    no: 'Hayır',
    never: 'Hiç değişmedi',
    pending: '{count} değişiklik bekliyor',
    apply: 'Değişiklikleri uygula',
    discard: 'Değişiklikleri at',
    confirmTitle: '{count} bayrak değişsin mi?',
    confirmBody: 'Değişiklik bütün işletmelerde bir sonraki istekte etkili olur: {changes}.',
    change: '{flag}: {from} → {to}',
    confirm: 'Bayrakları değiştir',
    reason: 'Gerekçe',
    reasonHint: 'Platform denetim kaydına yazılır.',
    saved: 'Bayraklar değiştirildi.',
    empty: 'Tanımlı bayrak yok.',
  },
  users: {
    title: 'Platform kullanıcıları',
    description: 'Backoffice\'e erişen platform ekibi. Her hesapta iki adımlı doğrulama zorunludur.',
    invite: 'Kullanıcı davet et',
    caption: 'Platform kullanıcıları',
    columns: {
      email: 'E-posta',
      role: 'Rol',
      status: 'Durum',
      mfa: 'İki adımlı doğrulama',
      lastSignIn: 'Son oturum açma',
      actions: 'Eylemler',
    },
    you: 'Siz',
    status: {
      Active: 'Etkin',
      Suspended: 'Askıda',
      Disabled: 'Devre dışı',
    },
    mfa: {
      on: 'Kurulu',
      off: 'Kurulmadı',
    },
    never: 'Hiç',
    rowActions: '{email} için eylemler',
    changeRole: 'Rolü değiştir',
    disable: 'Devre dışı bırak',
    selfHint: 'Kendi hesabınızda rol değişikliği ve devre dışı bırakma yapılamaz.',
    invitations: {
      title: 'Bekleyen davetler',
      caption: 'Kabul edilmemiş platform davetleri',
      email: 'E-posta',
      role: 'Rol',
      expires: 'Son geçerlilik',
      empty: 'Bekleyen davet yok.',
    },
    inviteSheet: {
      title: 'Platform kullanıcısı davet et',
      description: 'Davet bağlantısı yalnız e-postayla gider ve 7 gün geçerlidir. Aynı adrese yeni davet açık olanı iptal eder.',
      email: 'E-posta',
      emailRequired: 'E-posta adresini yazın.',
      emailInvalid: 'Geçerli bir e-posta adresi yazın.',
      role: 'Rol',
      submit: 'Daveti gönder',
      sent: 'Davet {email} adresine gönderildi.',
    },
    roleDialog: {
      title: '{email} için rol',
      description: 'Rol değişikliği kişinin açık oturumunda bir sonraki istekte geçerli olur.',
      submit: 'Rolü kaydet',
      saved: '{email} artık {role}.',
    },
    disableDialog: {
      title: '{email} devre dışı bırakılsın mı?',
      body: 'Kişinin platform oturumları hemen kapanır ve bir daha backoffice\'e oturum açamaz. İşletmelerdeki hesabı etkilenmez.',
      confirm: 'Devre dışı bırak',
      done: '{email} devre dışı bırakıldı.',
    },
    empty: 'Platform kullanıcısı yok.',
  },
  audit: {
    title: 'Denetim kaydı',
    description: 'Platform eylemleri ve platform oturumları, yeniden eskiye. Salt okumadır.',
    tenantDescription: 'Bu işletmeyi etkileyen platform eylemleri.',
    caption: 'Platform denetim kaydı',
    columns: {
      time: 'Zaman',
      action: 'Eylem',
      actor: 'Yapan',
      tenant: 'İşletme',
      reason: 'Gerekçe',
    },
    filters: {
      range: 'Tarih aralığı',
      actor: 'Yapan',
      action: 'Eylem',
      tenant: 'İşletme',
      tenantAll: 'Bütün işletmeler',
      tenantSearch: 'İşletme ara',
      tenantEmpty: 'İşletme bulunamadı.',
      tenantSearching: 'Aranıyor…',
      selectedActor: 'Seçilen kişi',
    },
    system: 'Sistem',
    unknownActor: 'Platform kullanıcısı ({id})',
    unknownTenant: 'İşletme ({id})',
    noTenant: '—',
    noReason: '—',
    empty: {
      title: 'Kayıt yok',
      body: 'Platform eylemleri burada görünür.',
    },
    detail: {
      time: 'Zaman',
      action: 'Eylem',
      actionCode: 'Eylem kodu',
      actor: 'Yapan',
      tenant: 'İşletme',
      entity: 'Kayıt',
      reason: 'Gerekçe',
      values: 'Değerler',
      before: 'Önce',
      after: 'Sonra',
      field: 'Alan',
      noValues: 'Bu kayıtta değer yok.',
      openTenant: 'İşletmeyi aç',
      showActor: 'Bu kişinin kayıtları',
      showTenant: 'Bu işletmenin kayıtları',
      valuesCaption: 'Eski ve yeni değerler',
      empty: '—',
    },
    actions: {
      TenantCreated: 'İşletme açıldı',
      TenantUpdated: 'İşletme bilgileri güncellendi',
      TenantSuspended: 'İşletme askıya alındı',
      TenantReactivated: 'İşletme yeniden açıldı',
      TenantClosed: 'İşletme kapatıldı',
      TenantStatusChanged: 'İşletmenin durumu değişti',
      TenantEntitlementsChanged: 'Özellik ya da kota geçersiz kılması değişti',
      TenantSubscriptionChanged: 'Abonelik değişti (plan, sürüm ya da ek paket)',
      PlanVersionPublished: 'Planın yeni sürümü yayınlandı',
      PlanPriceChangeScheduled: 'Planın fiyat değişikliği planlandı',
      PlanPriceChangeCanceled: 'Planın fiyat değişikliği geri alındı',
      AddOnVersionPublished: 'Ek paketin yeni sürümü yayınlandı',
      FeatureFlagsChanged: 'Platform bayrakları değişti',
      OutboxMessageReplayed: 'Teslim edilemeyen mesaj yeniden oynatıldı',
      OwnerReinvited: 'İşletme sahibi yeniden davet edildi',
      PlatformOwnerBootstrapped: 'İlk platform sahibi açıldı',
      PlatformUserInvited: 'Platform kullanıcısı davet edildi',
      PlatformInvitationAccepted: 'Platform daveti kabul edildi',
      PlatformUserRoleChanged: 'Platform kullanıcısının rolü değişti',
      PlatformUserDisabled: 'Platform kullanıcısı devre dışı bırakıldı',
      LoginSucceeded: 'Oturum açıldı',
      LoginFailed: 'Oturum açma başarısız',
      NewDeviceSignIn: 'Yeni cihazdan oturum açıldı',
      RefreshTokenReuseDetected: 'Oturum yenileme bilgisi yeniden kullanıldı; oturumlar kapatıldı',
      PasswordResetRequested: 'Şifre yenileme istendi',
      PasswordResetCompleted: 'Şifre yenilendi',
      PasswordChanged: 'Şifre değiştirildi',
      MfaEnabled: 'İki adımlı doğrulama açıldı',
      MfaDisabled: 'İki adımlı doğrulama kapatıldı',
      MfaRecoveryCodeUsed: 'Kurtarma kodu kullanıldı',
      MfaRecoveryCodesRegenerated: 'Kurtarma kodları yenilendi',
      LegalParameterSetDrafted: 'Yasal parametre taslağı açıldı',
      LegalParameterSetUpdated: 'Yasal parametre taslağı değişti',
      LegalParameterSetDeleted: 'Yasal parametre taslağı silindi',
      LegalParameterSetPublished: 'Yasal parametre sürümü yayınlandı',
      HolidayCalendarVersionDrafted: 'Tatil takvimi taslağı açıldı',
      HolidayCalendarVersionUpdated: 'Tatil takvimi taslağı değişti',
      HolidayCalendarVersionDeleted: 'Tatil takvimi taslağı silindi',
      HolidayCalendarVersionPublished: 'Tatil takvimi yayınlandı',
      OwnerRecoveryRequested: 'Sahip hesap kurtarma talebi açıldı',
      OwnerRecoveryApproved: 'Sahip hesap kurtarması onaylandı',
      OwnerRecoveryRejected: 'Sahip hesap kurtarması reddedildi',
      // S43 (bölüm 6): backoffice faturalama eylemleri (gerekçeli).
      BillingManualPaymentRecorded: 'Faturanın havale ödemesi elle kaydedildi',
      BillingRefundIssuedByPlatform: 'Faturanın ödemesi iade edildi',
      BillingChargeRetried: 'Faturanın tahsili elle yeniden denendi',
      BillingAlertResolved: 'Faturalama uyarısı elle kapatıldı',
      BillingFakeDeclineChanged: 'Test ödeme sağlayıcısında sonraki tahsillerin reddi değişti',
      BillingDunningGraceExtendedByPlatform: 'Tahsilat takibinde süre uzatıldı',
      // S62-b (SK-18): sistem dışında kesilen e-belge.
      BillingEInvoiceRecordedManually: 'Sistem dışında kesilen e-belge elle kaydedildi',
      BillingEInvoicePdfAttached: 'Elle kaydedilen e-belgenin PDF\'i yüklendi',
      // S44: destek oturumu (iki audit akışı; görüntülenen uçlar rota şablonu ve kayıt kimliğiyle).
      SupportSessionStarted: 'Destek oturumu başladı',
      SupportSessionEntered: 'Destek operatörü panele geçti',
      SupportSessionEnded: 'Destek oturumu bitti',
      SupportSessionViewed: 'Destek oturumunda görüntülendi',
      SupportSessionRequestRejected: 'Destek oturumunda istek reddedildi',
      // S44 (YH-S61, ASVS F-06): kapanan işletmenin iade paketi ve silinmesi; platform audit kaydının saklaması (sistem işleri).
      TenantReturnPackageReady: 'Kapanan işletmenin iade paketi üretildi',
      TenantDataDeleted: 'Kapanan işletmenin verisi silindi (imha tutanağı)',
      TenantDeletionHeld: 'Kapanan işletmenin silmesi iade paketi beklendiği için bekletildi',
      PlatformAuditRetentionApplied: 'Platform denetim kaydının saklaması uygulandı',
    },
    entities: {
      TenantExit: 'Kapanan işletme çıkışı',
      PlatformAuditLog: 'Platform denetim kaydı',
      Tenant: 'İşletme',
      PlatformInvitation: 'Platform daveti',
      PlatformMembership: 'Platform üyeliği',
      OutboxMessage: 'Outbox mesajı',
      FeatureFlag: 'Bayrak',
      FeatureFlags: 'Bayraklar',
      User: 'Hesap',
      Activation: 'Aktivasyon',
      LegalParameterSet: 'Yasal parametre sürümü',
      HolidayCalendarVersion: 'Tatil takvimi sürümü',
      OwnerRecoveryRequest: 'Sahip hesap kurtarma talebi',
      Plan: 'Plan sürümü',
      PlanAddOn: 'Ek paket sürümü',
      Invoice: 'Fatura',
      BillingAlert: 'Faturalama uyarısı',
      DunningCase: 'Tahsilat takibi dosyası',
      SupportSession: 'Destek oturumu',
    },
  },
  // S44 (YH-S61; 07 §13.7): kapanan işletmelerin silme kuyruğu. Yalnız izleme: pencere, iade paketi, silme durumu ve imha tutanağı.
  tenantExits: {
    title: 'Silme kuyruğu',
    description:
      'Kapanan işletmeler: iade paketinin durumu, indirme sayısı ve pencere sonunda silme. Silme işi saatte bir koşar; iade paketi hazır olmadan silme başlamaz. Burada eylem yoktur.',
    caption: 'Kapanan işletmelerin silme kuyruğu',
    filters: {
      label: 'Durum',
      All: 'Hepsi',
      Waiting: 'Pencere sürüyor',
      Deleting: 'Silme sürüyor ya da yarıda',
      Completed: 'Silindi',
      // S44 (YH-S61): pencere doldu ama iade paketi hazır değil ya da geç hazırlandı; silme bekletiliyor.
      Held: 'Paket bekleniyor',
    },
    columns: {
      tenant: 'İşletme',
      closedAt: 'Kapanış',
      windowEndsAt: 'Silme tarihi',
      package: 'İade paketi',
      downloads: 'İndirme',
      deletion: 'Silme',
    },
    package: {
      Pending: 'Hazırlanıyor',
      Ready: 'Hazır',
      Failed: 'Hazırlanamadı',
      Deleted: 'Silindi',
    },
    deletion: {
      Scheduled: 'Pencere sürüyor',
      InProgress: 'Sürüyor',
      Partial: 'Yarıda kaldı',
      Completed: 'Tamamlandı',
      Held: 'Paket bekleniyor',
    },
    empty: {
      title: 'Kapanan işletme yok',
      body: 'Bir işletme kapandığında iade paketi ve silme tarihiyle burada görünür.',
    },
    back: 'Silme kuyruğuna dön',
    notFoundTitle: 'Çıkış kaydı bulunamadı',
    notFoundBody: 'İşletme kapatılmamış ya da kayıt silinmiş olabilir.',
    errorNotice:
      'Paket hazırlanamadı ya da silme yarıda kaldı. Silme işi saatte bir paketi artan aralıklarla yeniden dener ve yarıda kalan modülden sürer; paket hazır olmadan veri silinmez.',
    // Kuyruk satırındaki uyarı simgesinin metni (ekran okuyucu; simge tek başına anlam taşımaz).
    errorFlag: 'paket ya da silme hatası var',
    summary: {
      title: 'Özet',
      closedAt: 'Kapanış',
      retention: 'Pencere',
      retentionDays: '{days} gün',
      windowEndsAt: 'Silme tarihi',
      package: 'İade paketi',
      size: 'Boyut',
      readyAt: 'Hazırlandığı an',
      downloads: 'İndirme',
      lastDownloadedAt: 'Son indirme',
      deletion: 'Silme',
      deletionStartedAt: 'Silme başladı',
      deletionCompletedAt: 'Silme bitti',
      sha256: 'Paketin SHA-256 özeti',
      packageError: 'Paket hatası',
      heldAt: 'Silme bekletildi',
      holdReason: 'Bekletme nedeni',
      none: '—',
    },
    contents: {
      title: 'Paketin içeriği',
      caption: 'Modül başına tablo ve dosya sayıları',
      module: 'Modül',
      tables: 'Tablo',
      rows: 'Kayıt',
      files: 'Dosya',
      empty: 'Paket henüz hazırlanmadı.',
    },
    progress: {
      title: 'Silme adımları',
      caption: 'Modül başına silme adımı',
      module: 'Modül',
      status: 'Durum',
      at: 'An',
      rows: 'Silinen kayıt',
      error: 'Hata',
      Done: 'Tamamlandı',
      Failed: 'Hata verdi',
      empty: 'Silme başlamadı.',
    },
    report: {
      title: 'İmha tutanağı',
      job: 'İş',
      runId: 'Koşu',
      startedAt: 'Başlangıç',
      completedAt: 'Bitiş',
      deletedRows: 'Silinen kayıt',
      legalHolds: 'Silme başladığında etkin yasal saklama',
      heldAt: 'Paket beklendiği için bekletildi',
      deletedTitle: 'Silinenler',
      deletedCaption: 'Modül ve tablo başına silinen kayıtlar',
      table: 'Tablo',
      rows: 'Kayıt',
      method: 'Yöntem',
      methods: {
        Deleted: 'Silindi',
        CryptoErased: 'Kripto-imha',
        Anonymized: 'Anonimleştirildi',
      },
      retainedTitle: 'Tutulanlar ve nedeni',
      retainedCaption: 'Silmede tutulan kayıtlar ve dayanakları',
      reason: 'Neden',
      notesTitle: 'Notlar',
      pending: 'Silme tamamlanınca imha tutanağı burada görünür.',
    },
  },
  reason: {
    required: 'Gerekçeyi yazın.',
  },
  errors: {
    unauthorized: 'Oturumunuzun süresi doldu. Devam etmek için yeniden oturum açın.',
    forbidden: 'Bu işlem platform rolünüze açık değil.',
    permission_denied: 'Bu işlem platform rolünüze açık değil. Gerekiyorsa platform sahibinden isteyin.',
    membership_inactive: 'Platform erişiminiz kapatıldı. Platform sahibine başvurun.',
    platform: {
      mfa_required: 'Bu işlem için iki adımlı doğrulamayla oturum açmanız gerekir.',
      ip_not_allowed: 'Bu ağdan platform yönetimine erişilemiyor.',
      access_denied: 'Platform yönetimine erişim doğrulanamadı. Sayfayı yenileyip tekrar deneyin.',
    },
    tenancy: {
      tenant: {
        not_found: 'İşletme bulunamadı. Listeyi yenileyin.',
        invalid_transition: 'İşletmenin durumu bu işleme uygun değil. Sayfayı yenileyip tekrar deneyin.',
      },
      // S41: plan kataloğu ve abonelik (`PlanErrors`).
      plan: {
        not_found: 'Plan bulunamadı. Sayfayı yenileyin.',
        version_not_found: 'Planın bu sürümü yok. Sürüm listesini yenileyin.',
        other_tenant: 'Bu plan başka bir işletmeye özel; bu işletmeye atanamaz.',
      },
      addon: {
        not_found: 'Ek paket bulunamadı. Sayfayı yenileyin.',
        not_available: 'Ek paket seçilen plana eklenemez ya da satışta değil.',
      },
      subscription: {
        not_found: 'İşletmenin aboneliği bulunamadı.',
      },
      // SK-28: planın fiyat değişikliği (`PlanPriceChangeErrors`).
      price_change: {
        notice_too_short: 'Fiyat değişikliği en az 30 gün önceden bildirilir; daha ileri bir ay başı seçin.',
        not_month_start: 'Fiyat değişikliği ayın ilk günü yürürlüğe girer.',
        pending: 'Bu planın yürürlüğe girmemiş bir fiyat değişikliği var; yenisini planlamak için önce onu geri alın.',
        no_new_version: 'Planın son sürümü zaten bir fiyat değişikliğinin hedefi. Yeni fiyatları önce yeni sürüm olarak yayınlayın.',
        quote_only: 'Teklif planının ya da aylık fiyatı olmayan planın fiyat değişikliği planlanamaz.',
        already_effective: 'Bu fiyat değişikliği yürürlüğe girmiş ya da geri alınmış; geri alınamaz. Sayfayı yenileyin.',
        not_found: 'Fiyat değişikliği bulunamadı. Sayfayı yenileyin.',
      },
    },
    // S43 (bölüm 6): genel katalogdaki faturalama iletileri İşletme sahibine yöneliktir; operatöre bunlar gösterilir. Diğer faturalama
    // kodlarında sunucunun Türkçe açıklaması (tutar ve son günle) yazılır.
    billing: {
      provider_disabled: 'Ödeme sağlayıcısı bu ortamda kapalı; karttan tahsil ve iade yapılamaz. Havaleyle gelen ödemeyi "Havale kaydet"le kaydedin.',
      payment_method: { missing: 'İşletmenin kayıtlı ödeme yöntemi yok. İşletme sahibi panelden kart eklemeli ya da ödeme havaleyle kaydedilmeli.' },
      invoice: { not_found: 'Fatura bulunamadı. Listeyi yenileyin.' },
      dunning: {
        not_found: 'İşletmenin açık tahsilat takibi yok; dosya bu arada kapanmış olabilir. Listeyi yenileyin.',
        grace_not_allowed: 'Askıya alınmış işletmede süre uzatılamaz. Ödeme alınınca ya da kaydedilince işletme yeniden açılır.',
      },
    },
    workforce: {
      owner_invitation: {
        tenant_not_found: 'İşletme bulunamadı.',
        tenant_inactive: 'Askıdaki ya da kapalı işletmenin sahibi davet edilemez. Önce işletmeyi yeniden açın.',
        owner_active: 'İşletmenin etkin bir sahibi var; yeniden davet gerekmez.',
        missing: 'Bu işletmede yenilenecek bir İşletme sahibi daveti yok.',
      },
      // S31 (YH-S52, S31-K2): sahip hesap kurtarma.
      owner_recovery: {
        tenant_not_found: 'İşletme bulunamadı.',
        tenant_inactive: 'Askıdaki ya da kapalı işletmede sahip hesabı kurtarılamaz. Önce işletmeyi yeniden açın.',
        no_owner: 'İşletmenin etkin bir İşletme sahibi yok. Kurtarma yerine sahibi yeniden davet edin.',
        multiple_owners: 'İşletmede birden çok İşletme sahibi var; diğer sahip, hesabı kaybedilen sahibin erişimini şirket içinden yenileyebilir.',
        owner_without_person: 'İşletme sahibinin çalışan kaydı yok; kurtarma yapılamıyor. Destek ekibiyle görüşün.',
        pending_exists: 'Bu işletmede karar bekleyen bir kurtarma talebi zaten var.',
        not_found: 'Kurtarma talebi bulunamadı. Listeyi yenileyin.',
        not_pending: 'Bu talebe karar verilmiş ya da talebin süresi dolmuş.',
        owner_changed: 'Talep açıldıktan sonra işletmenin sahibi değişti. Talebi reddedip yenisini açın.',
        same_operator: 'Talebi açan operatör karar veremez. Başka bir platform sahibi ya da yöneticisi onaylamalı.',
      },
      // S62-c (SK-19): kurtarma aktivasyonu sahibin yeni telefonuna SMS'le gider; SMS kapalıyken ya da işletme SMS'li planda değilken
      // (SK-29: deneme, Ücretsiz, ödemesi durmuş) onay tamamlanmaz (genel katalogdaki ileti şirketin İK'sına yöneliktir).
      activation: {
        sms_unavailable:
          'Kurtarma aktivasyonu sahibin yeni telefonuna SMS ile gittiği için onay tamamlanamadı: SMS kapalı ya da işletmenin planında SMS yok. Talep karar bekler; SMS gönderilebilir olunca yeniden onaylayın.',
      },
    },
    identity: {
      platform_user: {
        not_found: 'Platform kullanıcısı bulunamadı. Listeyi yenileyin.',
        role_invalid: 'Geçerli bir platform rolü seçin.',
        exists: 'Bu e-postayla açılmış bir platform kullanıcısı zaten var.',
        last_owner: 'Bu kişi platformun son sahibi. Önce başka bir kullanıcıyı platform sahibi yapın; sonra bu kişinin rolünü değiştirebilir ya da devre dışı bırakabilirsiniz.',
        disabled: 'Bu platform kullanıcısı zaten devre dışı.',
      },
      owner_recovery: {
        membership_not_found: 'İşletme sahibinin hesabı bulunamadı; kurtarma tamamlanamadı.',
      },
      platform_invitation: {
        invalid: 'Bağlantı geçersiz. Platform sahibinden yeni bir davet isteyin.',
        expired: 'Davetin süresi dolmuş. Platform sahibinden yeni bir davet isteyin.',
        used: 'Bu davet zaten kabul edildi. Oturum açabilirsiniz.',
      },
    },
  },
} as const;
